/**
 * The files behind one capture angle, and the way to put files into it.
 *
 * The coverage screen could say "Duvar 2: 12 dosya" and could not show one of
 * them, so "which twelve?" and "why is Duvar 3 empty when I photographed it?"
 * were both unanswerable here. Worse, the answer to the second is almost
 * always "they were uploaded before you picked an angle" - and there was no
 * way to fix that from the screen that reports it.
 *
 * Assignment goes one file at a time through the existing per-file endpoint,
 * bounded and with the partial result reported. Same rule as the session
 * screen's bulk actions: no batch route, ownership re-derived per id, and one
 * file's refusal does not decide the rest.
 */

import { useCallback, useState } from "react";

import { assignGuidedCoverageRegion, listGuidedCoverageMedia } from "@/api/client";
import { apiUrl } from "@/api/config";
import { ErrorState } from "@/components/ErrorState";
import { Button, Callout, Loading } from "@/components/ui";
import { assignFailureText, groupFailures, runBulk, type BulkResult } from "@/features/sessions/bulkMedia";
import { useAsync } from "@/lib/useAsync";

/** How many files one strip asks for. The endpoint's own ceiling is 200. */
const PAGE = 60;

export interface StripAction {
  label: string;
  /** The angle to mark with, or null to take the mark off. */
  region: string | null;
}

export function RegionMediaStrip({
  projectId,
  region,
  emptyText,
  actions,
  onChanged,
}: {
  projectId: string;
  /** A region key, or "unassigned" for files nobody has marked. */
  region: string;
  emptyText: string;
  actions: readonly StripAction[];
  /**
   * Something here changed the assignments.
   *
   * The panel above re-reads the region counts and remounts the *other*
   * strip - a file that leaves this one arrives in that one.
   */
  onChanged: () => void;
}) {
  const fetcher = useCallback(
    (signal: AbortSignal) => listGuidedCoverageMedia(projectId, { region, limit: PAGE }, { signal }),
    [projectId, region],
  );
  const { state, reload } = useAsync(fetcher);
  const [selection, setSelection] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<{ result: BulkResult; attempted: number; label: string } | null>(null);

  const files = state.data ?? [];
  const selected = files.filter((asset) => selection.has(asset.id)).map((asset) => asset.id);

  const apply = async (action: StripAction) => {
    setBusy(true);
    setReport(null);
    const attempted = selected.length;
    const result = await runBulk(selected, (id) => assignGuidedCoverageRegion(id, action.region));
    setBusy(false);
    setReport({ result, attempted, label: action.label });
    setSelection((current) => {
      const next = new Set(current);
      for (const id of result.succeeded) next.delete(id);
      return next;
    });
    // Even a partial change moves the numbers above this strip, and takes
    // the files that moved out of this list.
    if (result.succeeded.length > 0) {
      reload();
      onChanged();
    }
  };

  if (state.status === "loading" && state.data === null) {
    return <Loading label="Bölge dosyaları yükleniyor" />;
  }
  if (state.status === "error") return <ErrorState error={state.error} compact />;
  if (files.length === 0) return <p className="muted">{emptyText}</p>;

  return (
    <div className="stack-tight">
      <div className="row region-strip__head">
        <label className="region-strip__check">
          <input
            type="checkbox"
            checked={selected.length === files.length}
            ref={(node) => {
              if (node) node.indeterminate = selected.length > 0 && selected.length < files.length;
            }}
            onChange={(event) =>
              setSelection(event.target.checked ? new Set(files.map((asset) => asset.id)) : new Set())
            }
          />
          <span>Tümünü seç ({files.length})</span>
        </label>
        {files.length === PAGE && (
          // The endpoint answers a page. Saying "12 dosya" over a truncated
          // list would be a count of what was fetched, not of what is there.
          <span className="faint">İlk {PAGE} dosya gösteriliyor.</span>
        )}
      </div>

      <ul className="region-strip">
        {files.map((asset) => (
          <li key={asset.id} className={selection.has(asset.id) ? "region-strip__item region-strip__item--on" : "region-strip__item"}>
            <label>
              <input
                type="checkbox"
                checked={selection.has(asset.id)}
                onChange={(event) =>
                  setSelection((current) => {
                    const next = new Set(current);
                    if (event.target.checked) next.add(asset.id);
                    else next.delete(asset.id);
                    return next;
                  })
                }
              />
              <span className="visually-hidden">{asset.original_filename} dosyasını seç</span>
            </label>
            <img
              src={apiUrl(`/media/${encodeURIComponent(asset.id)}/preview`)}
              alt={`${asset.original_filename} önizlemesi`}
              loading="lazy"
              decoding="async"
            />
            <span className="region-strip__name" title={asset.original_filename}>
              {asset.original_filename}
            </span>
          </li>
        ))}
      </ul>

      {selected.length > 0 && (
        <div className="row">
          {actions.map((action) => (
            <Button key={action.label} busy={busy} disabled={busy} onClick={() => void apply(action)}>
              {action.label} ({selected.length})
            </Button>
          ))}
        </div>
      )}

      {report && <AssignmentReport {...report} />}
    </div>
  );
}

function AssignmentReport({
  result,
  attempted,
  label,
}: {
  result: BulkResult;
  attempted: number;
  label: string;
}) {
  const reasons = groupFailures(result.failed, assignFailureText);
  return (
    <Callout
      tone={result.failed.length > 0 ? "caution" : "success"}
      title={`${result.succeeded.length} / ${attempted} dosya için "${label}" uygulandı.`}
    >
      {result.failed.length === 0 ? (
        <span>Tümü tamamlandı.</span>
      ) : (
        <ul className="bulkbar__reasons">
          {reasons.map((row) => (
            <li key={row.reason}>
              {row.count} dosya: {row.reason}
            </li>
          ))}
        </ul>
      )}
    </Callout>
  );
}
